import { contentClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import type { CatalogPage, ContentPage } from '../types/catalog-v2.types';
import type {
  ContentEnvelope,
  ContentListEnvelope,
  FailedProduct,
  FeedAuditProductChange,
  FeedAuditSummary,
  ProductPerformance,
  ProductScoreDetails,
  ProductScoreStatus,
  RunEnrichedProduct,
  RunScorecard,
  SamplePreviewProduct,
  SampleQualityScore,
  ScorePage,
} from '../types/run-content.api.types';
import { toSearchParams } from '../utils/envelope';

/** Paging params every content-generation gate list takes. */
interface PageParams {
  page: number;
  size: number;
}

/**
 * `includeTotal: false` skips the server's whole-run count for a page whose total the caller
 * already has (a walk's pages after the first); an older content-gen ignores it and counts.
 */
interface CountParams {
  includeTotal?: boolean | undefined;
}

/** Normalises a content-generation list envelope into the catalog-v2 UI page shape. */
function toContentPage<T>(body: ContentListEnvelope<T>): ContentPage<T> {
  return {
    items: body.data,
    total: body.pagination.total,
    page: body.pagination.page,
    size: body.pagination.size,
    totalPages: body.pagination.totalPages,
    nextCursor: body.pagination.nextCursor ?? null,
    hasNextPage: body.pagination.hasNextPage ?? null,
  };
}

/**
 * Scores for an explicit set of catalog products (content-gen needs the ids —
 * one call per page, never per product). `phase` PRE | POST | SAMPLE_POST. `missingReasons`
 * (R7) adds why each unscored id has no score; an older content-gen ignores the flag.
 */
export async function getRunScores(
  runRef: string,
  productIds: string[],
  phase: 'PRE' | 'POST' | 'SAMPLE_POST',
  options: { missingReasons?: boolean } = {},
): Promise<ScorePage | null> {
  const searchParams = new URLSearchParams({ phase });
  for (const id of productIds) searchParams.append('productIds', id);
  if (options.missingReasons) searchParams.set('missingReasons', 'true');
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.scores(runRef), { searchParams })
    .json<ContentEnvelope<ScorePage>>();
  return body.data;
}

/** The run's PRE / POST score roll-up; each block degrades alone via `unavailable`. */
export async function getRunScorecard(runRef: string): Promise<RunScorecard | null> {
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.scorecard(runRef))
    .json<ContentEnvelope<RunScorecard>>();
  return body.data;
}

/** A page of the newest SAMPLE attempt's products, before / after. */
export async function getSamplePreview(
  runRef: string,
  params: PageParams,
): Promise<CatalogPage<SamplePreviewProduct>> {
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.samplePreview(runRef), {
      searchParams: toSearchParams({ ...params }),
    })
    .json<ContentListEnvelope<SamplePreviewProduct>>();
  return toContentPage(body);
}

/** The sample's headline before / after / lift and per-dimension deltas. */
export async function getSampleQualityScore(runRef: string): Promise<SampleQualityScore | null> {
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.sampleQualityScore(runRef))
    .json<ContentEnvelope<SampleQualityScore>>();
  return body.data;
}

/** Most ids one grid read's `productIds` filter may name (content-gen 422s at 101). */
export const GRID_PRODUCT_ID_LIMIT = 100;

/**
 * R1 — optional server filters on `enriched-products`, applied before the page is cut (so the
 * total is the filtered total). Without any of them the read is exactly as before.
 */
export interface EnrichedProductsFilter {
  /** Only these catalog products (≤ 100, sent as a repeated key). */
  productIds?: readonly string[] | undefined;
  /** Only products whose run WROTE a non-empty value for this catalog attribute code. */
  field?: string | undefined;
  /** The row's score-overlay tier (`scores.tier`). */
  verdict?: 'PASS' | 'REVIEW' | 'FAIL' | undefined;
  /** By the overlay's post − pre (`delta`) or post score; `sortOrder` the direction. */
  order?: 'delta' | 'post' | undefined;
  sortOrder?: 'ASC' | 'DESC' | undefined;
  /** B10 — title (as the run read it) / offer id contains, or product id starts with (1–200). */
  search?: string | undefined;
  /** B10 — the brand the run read for the product, exactly. */
  brand?: string | undefined;
  /** B10 — the external id's country (`en~US~SKU` → `US`), exactly. */
  market?: string | undefined;
  /** B10 — effective value present and different from / equal to the before value (a text
   *  attribute code; a record / number / unknown code is a 422). */
  changed?: string | undefined;
  unchanged?: string | undefined;
  /** B10 — one of the server's exact review reasons (an unknown value is a 422). */
  reason?: ServerReviewReason | undefined;
  /** B10 — 0–100 bounds on the overlay's pre / post overall; min inclusive, max exclusive. */
  preMin?: number | undefined;
  preMax?: number | undefined;
  postMin?: number | undefined;
  postMax?: number | undefined;
}

/** B10 — the review reasons content-gen can state exactly on `enriched-products?reason=`. */
export type ServerReviewReason =
  | 'SCORE_DROPPED'
  | 'SCORER_FLAGGED'
  | 'TITLE_TOO_LONG'
  | 'DESCRIPTION_TOO_SHORT'
  | 'DESCRIPTION_TOO_LONG';

/** Page + filter params as search params, `productIds` repeated (≤ 100). */
function gridSearchParams(
  params: Record<string, string | number | boolean | null | undefined>,
  productIds: readonly string[] | undefined,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(toSearchParams(params))) {
    searchParams.set(key, String(value));
  }
  for (const id of (productIds ?? []).slice(0, GRID_PRODUCT_ID_LIMIT)) {
    searchParams.append('productIds', id);
  }
  return searchParams;
}

/** A page of the products the run enriched successfully (R1 filters optional). */
export async function getEnrichedProducts(
  runRef: string,
  params: PageParams & CountParams & EnrichedProductsFilter,
): Promise<ContentPage<RunEnrichedProduct>> {
  const { productIds, ...rest } = params;
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.enrichedProducts(runRef), {
      searchParams: gridSearchParams({ ...rest }, productIds),
    })
    .json<ContentListEnvelope<RunEnrichedProduct>>();
  return toContentPage(body);
}

/** content-generation's enrichment phase: the sample pass or the full pass. */
export type EnrichmentPhase = 'SAMPLE' | 'FULL';

/** Optional narrowing on the failed-products read (server default: FULL once it exists). */
export interface FailedProductsFilter {
  phase?: EnrichmentPhase;
  /** Only failures a retry would re-run. */
  retryable?: boolean;
  /** R1 — only these catalog products (≤ 100, repeated key). */
  productIds?: readonly string[] | undefined;
  /** R1 — one failure category (`failureCategory`, case-insensitive on the server). */
  reason?: string | undefined;
}

/** A page of the products the run failed to enrich — the RETRY_FAILED_ITEMS candidates. */
export async function getFailedProducts(
  runRef: string,
  params: PageParams & CountParams & FailedProductsFilter,
): Promise<ContentPage<FailedProduct>> {
  const { productIds, ...rest } = params;
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.failedProducts(runRef), {
      searchParams: gridSearchParams({ ...rest }, productIds),
    })
    .json<ContentListEnvelope<FailedProduct>>();
  return toContentPage(body);
}

/**
 * Read-back for an on-demand re-score started on catalog-service
 * (`postProductRescore`). 404 until content-generation has a re-score request
 * or result for this product.
 */
export async function getProductScore(
  runRef: string,
  productId: string,
): Promise<ProductScoreStatus | null> {
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.productScore(runRef, productId))
    .json<ContentEnvelope<ProductScoreStatus>>();
  return body.data;
}

/** The score phases content generation keeps a set for. */
export type ScorePhase = 'PRE' | 'POST' | 'SAMPLE_POST';

/**
 * One product's score breakdown for one phase (dimensions, per-field scores with their
 * feedback, the scored feed values). A product with no row in that set answers 404 —
 * returned as null so the panel can say "not scored" instead of showing an error.
 * `onDemand` reads the product's on-demand re-score set instead of the pipeline's.
 */
export async function getProductScoreDetails(
  runRef: string,
  productId: string,
  phase: ScorePhase,
  onDemand = false,
): Promise<ProductScoreDetails | null> {
  try {
    const body = await contentClient
      .get(endpoints.contentCatalogRuns.scoreDetails(runRef, productId), {
        searchParams: onDemand ? { phase, onDemand: true } : { phase },
      })
      .json<ContentEnvelope<ProductScoreDetails>>();
    return body.data;
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/** A catalog-ops read that 404s for "not linked / not enriched" — answered as null, never thrown. */
async function readOrNull<T>(read: () => Promise<ContentEnvelope<T>>): Promise<T | null> {
  try {
    return (await read()).data;
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/** I10 — what a run changed in the feed (FEED_AUDIT flow); null when content-gen has no link. */
export async function getFeedAudit(runRef: string): Promise<FeedAuditSummary | null> {
  return readOrNull(() =>
    contentClient
      .get(endpoints.contentCatalogRuns.feedAudit(runRef))
      .json<ContentEnvelope<FeedAuditSummary>>(),
  );
}

/** I10 — one page of every changed product, heaviest changers first (size ≤ 200). */
export async function getFeedAuditProducts(
  runRef: string,
  params: PageParams,
): Promise<CatalogPage<FeedAuditProductChange>> {
  const body = await contentClient
    .get(endpoints.contentCatalogRuns.feedAuditProducts(runRef), {
      searchParams: toSearchParams({ page: params.page, size: Math.min(params.size, 200) }),
    })
    .json<ContentListEnvelope<FeedAuditProductChange>>();
  return toContentPage(body);
}

/** I11 — one product's AI-performance drill-down; null when the run never enriched it (404). */
export async function getProductPerformance(
  runRef: string,
  productId: string,
): Promise<ProductPerformance | null> {
  return readOrNull(() =>
    contentClient
      .get(endpoints.contentCatalogRuns.productPerformance(runRef, productId))
      .json<ContentEnvelope<ProductPerformance>>(),
  );
}
